import { useT, type I18nKey } from "@/lib/i18n";

/**
 * Shared form-field error renderer. Sits beneath each `<Input>` in
 * the auth pages (Login, SignUp, Forgot, Reset, etc.).
 *
 * Translates Zod messages on the fly: schemas in `packages/shared`
 * use stable i18n keys (e.g. `"validation.email_invalid"`) as the
 * `.message` strings, so a Zod issue bubbling up here lands as
 * `errors[0].message === "validation.email_invalid"`. We detect the
 * `validation.` prefix and run it through `useT()` to render the
 * localized copy. Anything else (a free-form server-side message,
 * a custom `.refine` message that wasn't keyed) is rendered
 * verbatim — graceful degradation, no broken UI.
 *
 * `errors` is intentionally permissively typed: TanStack Form's
 * Zod adapter passes through `{ message }` issue objects, but
 * older callsites sometimes pass raw strings, and `undefined`
 * slots show up between renders.
 */
export function FieldError({
  errors,
  touched,
}: {
  errors: Array<{ message?: string } | string | undefined>;
  touched: boolean;
}) {
  const t = useT();
  if (!touched || errors.length === 0) return null;

  const messages = errors
    .map((e) => (typeof e === "string" ? e : e?.message))
    .filter((m): m is string => Boolean(m))
    .map((m) => translateIfKey(t, m));

  if (messages.length === 0) return null;

  return (
    <p className="mt-1 text-[12px] text-[color:var(--again)]" role="alert">
      {messages.join(", ")}
    </p>
  );
}

/**
 * If the message looks like one of our i18n keys (starts with
 * `validation.`), localize it; otherwise return verbatim. Centralized
 * so future error sources (server-side validation feedback, custom
 * refines) get the same treatment without each callsite having to
 * branch on the prefix.
 */
function translateIfKey(t: (key: I18nKey) => string, message: string): string {
  if (message.startsWith("validation.")) {
    // The cast is the soft handshake between Zod's loose `.message:
    // string` and our `I18nKey` union. If the key is missing from
    // the dictionary, `useT()` warns in dev and renders the key
    // verbatim — which is still an improvement over the original
    // English Zod default.
    return t(message as I18nKey);
  }
  return message;
}
